// AdminOnlyRoute — the ONLY gate the moved `/Penelopesims` admin
// route uses. Presents an indistinguishable 404 when the caller
// isn't a verified admin, so probes at `/admin` (or a non-admin
// visitor stumbling onto `/Penelopesims`) get NO evidence that the
// route exists at all — no login page, no redirect, no flash.
//
// Threat model:
//   * A crawler / attacker hitting `/admin` sees "Page not found." —
//     same string the reserved-slug 404 uses on `/:slug`. Zero signal.
//   * A logged-in but non-admin user (agent, athlete, brand) hitting
//     `/Penelopesims` gets the same 404. Client-side gate matches
//     the server's `require_founder`/`get_current_admin` rules.
//   * Real security still lives in `Depends(get_current_admin)` on
//     every `/api/admin/*` endpoint — this component is JUST the URL
//     obfuscation + UX hide. Server never trusts client claims.
//
// Admin roles per backend/server.py:2747 → {"admin","founder","editor"}

import { useAuth } from '../contexts/AuthContext';

const ADMIN_ROLES = new Set(['admin', 'founder', 'editor']);

const _NotFound = () => (
  <div
    className="min-h-screen bg-black text-white flex items-center justify-center text-sm text-white/50"
    data-testid="admin-gate-notfound"
  >
    Page not found.
  </div>
);

const AdminOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();

  // While the /auth/me probe is in flight, render nothing (blank).
  // Deliberately NOT a branded spinner — a distinctive loading UI
  // would leak "there's something here". A blank screen matches
  // network latency indistinguishably from a real 404 fetch.
  if (loading) {
    return <div className="min-h-screen bg-black" />;
  }

  // Not signed in → 404. Signed in but wrong role → 404.
  const role = (user?.role || '').toLowerCase();
  const isAdmin = !!user && (user.is_admin === true || ADMIN_ROLES.has(role));
  if (!isAdmin) return <_NotFound />;

  return children;
};

export default AdminOnlyRoute;
